<template>
  <div class="container v-content-container" :key="componentKey">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          {{ $label("customer.customer_groups", "Customer groups") }}
          <router-link
            :to="{ name: 'NewGroup' }"
            class="btn btn-v-lightgreen-white pull-right"
            v-tooltip="{
              content: $label('group.new_group', 'New group'),
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label(
          "group.group_failed_request",
          "Request failed while loading groups."
        )
      }}
    </alert-box>

    <Table
      v-show="!failedRequest"
      id="groups-table"
      :loading="loading"
      :columns="[
        {
          title: 'Group',
          field: 'name',
          id: row => `group-name-${row.id}`
        },
        {
          title: 'Description',
          field: 'description',
          id: row => `group-description-${row.id}`
        },
        {
          title: 'Customers',
          field: 'customers_count',
          id: row => `group-customers-count-${row.id}`
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'GroupDetails',
              params: { group_id: row.id }
            });
          }
        },
        {
          icon: 'envelope-o',
          bgColor: 'primary',
          tooltip: row => `Email to this customer group`,
          onClick: () => {
            $router.push('/customers/group/groupId/message/email/');
          }
        },
        {
          icon: 'mobile',
          bgColor: 'primary',
          tooltip: row => `SMS to this customer group`,
          onClick: () => {
            $router.push('/customers/group/groupId/message/sms/');
          }
        },
        {
          type: 'delete',
          id: row => `delete-group-button-${row.id}`,
          onClick: row => deleteGroup(row.id)
        }
      ]"
      :data="groups"
      :meta="metaData"
      @fetchQuery="
        params => {
          fetchParams = params;
          getGroups({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewGroup' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{ $label("group.new_group", "New group") }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import swal from "sweetalert";

import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";

export default {
  name: "Groups",

  components: {
    Table,
    AlertBox
  },

  data() {
    return {
      groups: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      },
      selectedGroupId: null,
      componentKey: 0
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.group_deleted === true ||
        vm.$route.query.group_deleted === "true"
      ) {
        swal("Success!", "The group was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getGroups(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.group_deleted === true) {
      swal("Success!", "The group was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getGroups(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getGroups(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.groups = [];
      const endpoint = this.$partnerEndpoint("groups");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search
          }
        })
        .then(response => {
          this.groups = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteGroup(groupId) {
      this.selectedGroupId = groupId;
      if (this.loading || this.updating || this.selectedGroupId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The group will be deleted.",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "groups/" + this.selectedGroupId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "GroupsOverview",
                  query: { group_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The group was deleted.", "success");
                    this.getGroups(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete groups.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}
</style>
